import {useContext, FC, useState, useRef, useLayoutEffect, MutableRefObject} from 'react';
import ImageComponent from "../../utilities/ImageComponent";
import IframeVideo from "../../utilities/VideoUtils/IframeVideo";
import {
    getFormattedEventData,
    getTimezoneAbbr,
    getVideoElementTypeFromItemType,
} from "../../../utils/CleversiteUtilities";
import Html5Video from "../../utilities/VideoUtils/Html5Video";
import moment from "moment";
import {capitalize, getPlatform, getSocialIcon} from "../../../utils/StringUtilities";
import styles from "./styles/ListItemGroup.module.scss";
import FlatIcon from "../../utilities/FlatIcon";
import FontAwesome from "../../utilities/FontAwesome";
import {observer} from "mobx-react";
import {modalStore, StoreContext} from "../../../stores/StoreLoader";
import classNames from 'classnames';
import {getContentItemIcon} from "../../blocks/TerracedGridTheme/FilesFoldersBlock/fileUtilities";
import ClickableLink from "../../utilities/ClickableLink";
import SocialMarkdown from "../../utilities/SocialMarkdown";
import {sourceTypesEnum} from "../../../utils/SourceUtils";
import {IReactiveEventContentItem, IReactiveNewsContentItem} from "../../../stores/SchoolFeedStore";
import {SchoolFeedStoreContext} from "../SchoolFeedWidget";
import SafeRouter from "../../utilities/SafeRouter";
import {isDescendantOrEq} from "../../../utils/DataUtilities";

export function isEvent(item: EventContentItem | ContentItem): item is EventContentItem {
    return (item as EventContentItem).is_event === true;
}

function isMedia(item: MediaContentItem | ContentItem): item is MediaContentItem {
    return (item as MediaContentItem).is_media === true;
}

export function isFile(item: FileContentItem | ContentItem): item is FileContentItem {
    return (item as FileContentItem).is_file === true;
}

export function isNews(item: NewsContentItem | ContentItem): item is NewsContentItem {
    return (item as NewsContentItem).is_news === true;
}

const handleOpenNewsItemModal = itemObj => {
    if (/drive.google.com/.test(itemObj.third_party_url)) {
        modalStore.addModal({
            type: "file-preview",
            id: itemObj.id,
            itemObj: { // need to spoof a file item here
                ...itemObj,
                json_data: {
                    file: {
                        view_link: itemObj.third_party_url,
                    },
                },
            },
        })
    } else if (isNews(itemObj)) { // use test type when creating new source to not allow open modal
        modalStore.addModal({
            type: "news",
            id: itemObj.id,
            itemObj: itemObj,
        })
    } else if (isEvent(itemObj)) {
        modalStore.addModal({
            type: "events",
            itemObj: itemObj,
            id: itemObj.id,
        });
    }
};

const Item = observer((props: {
    itemObj: IReactiveNewsContentItem | IReactiveEventContentItem,
    allowCuration: boolean,
}) => {
    const schoolFeedStore = useContext(SchoolFeedStoreContext);
    const {userStore, organizationStore} = useContext(StoreContext);
    const itemRef: MutableRefObject<any> = useRef(null);
    let formatted;

    if (props.itemObj.is_event && (props.itemObj as IReactiveEventContentItem).json_data.event) {
        formatted = getFormattedEventData(props.itemObj, undefined, "h:mma");
    }

    const isGoogleDriveFile = /drive.google.com/.test(props.itemObj.third_party_url);

    const [shortened, setShortened] = useState(false);

    useLayoutEffect(() => {
        setShortened(itemRef?.current?.clientHeight > 350);
    }, []);

    const itemClassName = classNames({
        [styles.item]: true,
        [styles.itemHidden]: !props.itemObj.published,
        [styles.itemClickable]: isGoogleDriveFile,
        [styles.itemShortened]: shortened,
    })

    let itemDetails;
    if (isEvent(props.itemObj)) {
        itemDetails = <div className={styles.dateLocationContainer}>
            {formatted?.timeStr && <div>
                <FlatIcon name={'flaticon-clock'}/>&nbsp;<span>{formatted.timeStr}</span>&nbsp;<span>{formatted.timeStr !== "All Day" && getTimezoneAbbr()}</span>
            </div>}
            {props.itemObj.json_data.event.location && <div role={"button"} onClick={() => modalStore.addModal({
                type: "events",
                itemObj: props.itemObj,
                id: props.itemObj.id,
            })}>
                <FlatIcon name={'flaticon-map'}/> {props.itemObj.json_data.event.location}
            </div>}
        </div>
    } else if (isFile(props.itemObj)) {
        const iconUrl = getContentItemIcon(props.itemObj);
        itemDetails = <div className={styles.dateLocationContainer}>
            <ClickableLink href={props.itemObj.json_data.file.view_link}>
                <img src={iconUrl} alt={"File Icon"} />
                Click to View File
            </ClickableLink>
        </div>
    }

    let sourceIconLink;
    const platform = getPlatform(props.itemObj.type);
    if (props.itemObj.third_party_url && ![sourceTypesEnum.ICAL, sourceTypesEnum.RSS].includes(platform)) {
        const iconClassName = classNames({
            [styles[`sourceIcon${capitalize(platform)}`]]: true,
            [styles.sourceIcon]: true,
        })
        sourceIconLink = <ClickableLink className={iconClassName} href={props.itemObj.third_party_url} title={props.itemObj.source.account_title}>
            <FontAwesome name={getSocialIcon(platform, true)} />
        </ClickableLink>
    }

    const currentUserCanModifyAtCurrentOrganization = props.itemObj.organization && isDescendantOrEq(props.itemObj.organization.path, organizationStore.currentOrganization.path);

    return <div className={itemClassName} ref={itemRef} onClick={() => {
        if (shortened) {
            setShortened(false);
        } else if (isGoogleDriveFile) {
            modalStore.addModal({
                type: "file-preview",
                id: props.itemObj.id,
                itemObj: { // need to spoof a file item here
                    ...props.itemObj,
                    json_data: {
                        file: {
                            view_link: props.itemObj.third_party_url,
                        },
                    },
                },
            })
        }
    }}>
        {userStore.editor && props.allowCuration && <div className={styles.curationContainer}>
            {props.itemObj.is_custom && <button onClick={async () => {
                await SafeRouter.setQueries(["spview", "SchoolFeedPost"], ["id", props.itemObj.id])
            }}>
                <FlatIcon name='flaticon-pen'/>
            </button>}
            <button onClick={e => {
                e.stopPropagation();
                schoolFeedStore.toggleHideItem(props.itemObj, props.itemObj.organization_id)
            }} title={currentUserCanModifyAtCurrentOrganization ? "Toggle Item Visibility" : "You do not have permission to toggle item visibility at current SchoolFeed level."} disabled={!currentUserCanModifyAtCurrentOrganization}>
                <FontAwesome ariaHidden={true} prefix={'far'} name={props.itemObj.published ? 'fa-eye' : 'fa-eye-slash'}/>
            </button>
            {props.itemObj.is_custom && <button onClick={e => {
                e.stopPropagation();
                schoolFeedStore.deleteItem(props.itemObj, props.itemObj.organization_id)
            }} title={currentUserCanModifyAtCurrentOrganization ? "Delete Content Item" : "You do not have permission to delete item at current SchoolFeed level."} disabled={!currentUserCanModifyAtCurrentOrganization}>
                <FlatIcon ariaHidden={true} name={'flaticon-remove'}/>
            </button>}
        </div>}
        <div className={styles.header}>
            {props.itemObj.title && <div className={styles.title}><SocialMarkdown platform={platform} text={props.itemObj.title} /></div>}
            {isEvent(props.itemObj) && itemDetails}
            {props.itemObj.description && <div className={styles.description}>{sourceIconLink}<SocialMarkdown text={props.itemObj.description} platform={platform} /></div>}
            {isFile(props.itemObj) && itemDetails}
        </div>
    </div>
});

const ListItemGroup: FC<{
    items: Array<IReactiveEventContentItem | IReactiveNewsContentItem>,
    allowCuration: boolean,
    showMedia: boolean,
}> = observer(props => {

    const firstItem = props.items[0];
    let media;
    if (isMedia(firstItem) && firstItem.json_data.videos && firstItem.json_data.videos.videos_list) {
        const type = getVideoElementTypeFromItemType(firstItem.type);
        media = type === "iframe" ?
            <IframeVideo embedLink={firstItem.json_data.videos.videos_list[0].raw_url}/> :
            <Html5Video embedLink={firstItem.json_data.videos.videos_list[0].raw_url}/>;
    } else if (firstItem.image) {
        const imageIsSourceImage = firstItem.image === firstItem.source?.account_image || firstItem.image === firstItem.source?.account_image?.replace("_normal", "");
        media = <ImageComponent data-smallwidth={imageIsSourceImage} src={firstItem.image} alt={firstItem.title || `Image from ${getPlatform(firstItem.type)}`}/>
    } else if (isEvent(firstItem) && firstItem.json_data.event) {
        const start = moment(firstItem.json_data.event.start);
        const end = moment(firstItem.json_data.event.end);

        const startMonth = start.format("MMM");
        const startDay = start.format("D");

        if (firstItem.json_data.event.multi_day) {
            const endMonth = end.format("MMM");
            const endDay = end.format("D");

            if (startMonth === endMonth) {
                media = <div className={styles.dateMedia}>
                    <span>{startMonth}</span>
                    <span>{startDay} - {endDay}</span>
                </div>
            } else {
                media = <div className={styles.dateMedia}>
                    <span>{startMonth} {startDay} -</span>
                    <span>{endMonth} {endDay}</span>
                </div>
            }
        } else {
            media = <div className={styles.dateMedia}>
                <span>{startMonth}</span>
                <span>{startDay}</span>
            </div>
        }

    }

    return <div className={styles.groupContainer}>
        {props.showMedia && media}
        <div className={styles.contentContainer}>
            {props.items.map(item => <Item allowCuration={props.allowCuration} key={item.id} itemObj={item}/>)}
            {firstItem.organization && <div className={styles.organizationTitle}>
                {firstItem.organization.title}
            </div>}
        </div>
    </div>
})

export default ListItemGroup